import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';

import type { PushNotificationModel } from '@portal-clients/push-notification';

/**
 * Early push notification handling is a solution to the issue PDN-4371.
 * https://pej.atlassian.net/browse/PDN-4371
 *
 * On iOS, when the user taps a notification to launch a shut-down app,
 * the OS calls userNotificationCenter(_:didReceive:withCompletionHandler:)
 * once, early in the native app lifecycle. Capacitor's iOS plugin
 * processes that delegate callback and emits pushNotificationActionPerformed
 * to the WebView as soon as the WebView finishes loading — a one-shot event.
 * If nothing is listening at that exact moment, the event is gone.
 *
 * On Android, the notification tap data arrives as an Android Intent on the
 * Activity. Intents persist until explicitly consumed. The Capacitor
 * Android plugin inspects getIntent() when the plugin initialises, which
 * means it can re-deliver the event data even after the JS side registers
 * its listener. So on Android cold-start works correctly.
 *
 * Essentially on iOS we listen early to intercept and store the notification
 * until the app has initialized whereas on Android this solution is a no-op
 * and is not needed.
 */

let pendingNotification: PushNotificationModel | null = null;
let earlyListenerHandle: Promise<{ remove: () => Promise<void> }> | null = null;

export const registerEarlyPushNotificationListener = (): void => {
  if (
    !Capacitor.isNativePlatform() ||
    !Capacitor.isPluginAvailable('PushNotifications')
  ) {
    return;
  }

  earlyListenerHandle = PushNotifications.addListener(
    'pushNotificationActionPerformed',
    (notification) => {
      pendingNotification = notification.notification as PushNotificationModel;
    },
  );
};

export const removeEarlyPushNotificationListener = async (): Promise<void> => {
  if (!earlyListenerHandle) {
    return;
  }

  const handle = earlyListenerHandle;
  earlyListenerHandle = null;

  await (await handle).remove();
};

export const consumePendingLaunchNotification =
  (): PushNotificationModel | null => {
    const notification = pendingNotification;
    pendingNotification = null;

    return notification;
  };
